import { useState, useEffect } from 'react';
import { apiFetch } from '../api/client';
import { AlertTriangle } from 'lucide-react';
import { HarmonicSeriesCell, HarmonicCellData } from './HarmonicSeriesCell';
import { DataUnavailable } from './DataState';

// Impact View alarm-violation roster: one row per POINT (worst axis) that has
// violated an Impact View (PeakVue) alarm in the trailing window. Read-only view
// over /machines/impact-alarm-violations — no scoring path is involved.

interface RocData {
  roc_state: string;
  slope_pct_wk: number | null;
  r2: number | null;
  n_captures: number;
}

interface ViolationRow {
  machine_id: string;
  machine_name: string;
  area_name: string;
  company_name: string;
  db_id: number;
  point_index: number;
  point_name: string;
  axis: string;
  axis_num: number;
  threshold_moderate: number;
  threshold_severe: number;
  is_emerson_fallback: boolean;
  violations_moderate_7d: number;
  violations_severe_7d: number;
  latest_max_peak_g: number | null;
  latest_severity: string | null;
  periodic_pct: number | null;
  concentration_ratio: number | null;
  harmonic_fraction: number | null;
  measured_at: string | null;
  maxpk_roc: RocData | null;
  harmonic_column: HarmonicCellData | null;
  confidence: string;
}

interface ViolationResponse {
  window_days: number;
  count: number;
  points: ViolationRow[];
}

// measured_at is 'YYYY/MM/DD HH:MM:SS' — already Eastern wall-clock. Reformat
// directly (NO tz shift); this is NOT the UTC-ISO calculated_at path.
function fmtEastern(ts: string | null): string {
  if (!ts) return '—';
  const m = ts.match(/^(\d{4})\/(\d{2})\/(\d{2})[ T](\d{2}):(\d{2})/);
  if (!m) return ts;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function ColTooltip({ text }: { text: string }) {
  return (
    <span className="relative group inline-flex items-center ml-0.5" onClick={e => e.stopPropagation()}>
      <span className="w-3.5 h-3.5 rounded-full bg-gray-300 text-white text-[9px] font-bold flex items-center justify-center cursor-help leading-none shrink-0">?</span>
      <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 bg-gray-800 text-white text-xs rounded px-2.5 py-2 leading-snug opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 shadow-lg whitespace-normal text-left font-normal normal-case tracking-normal">
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-800" />
        {text}
      </span>
    </span>
  );
}

function ConfidencePip({ level }: { level: string }) {
  const colors = { high: '#16a34a', medium: '#d97706', low: '#94a3b8' };
  const color = colors[level as keyof typeof colors] ?? '#94a3b8';
  return (
    <span className="inline-flex items-center gap-1 text-xs" style={{ color }}>
      <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: color }} />
      {level}
    </span>
  );
}

// Sort keys. Default = severity: severe violations, then moderate, then latest MaxPk.
type SortKey =
  | 'severity' | 'machine_name' | 'threshold' | 'latest_max_peak_g'
  | 'periodic_pct' | 'concentration_ratio' | 'harmonic_fraction'
  | 'confidence' | 'measured_at';

interface Props {
  onMachineClick: (machineId: string, machineName: string, points: any[], imageUrl?: string, initialPointName?: string) => void;
}

export function BearingHealth({ onMachineClick }: Props) {
  const [rows, setRows] = useState<ViolationRow[]>([]);
  const [windowDays, setWindowDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dbFilter, setDbFilter] = useState<number | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('severity');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    setLoading(true);
    setError(false);
    apiFetch<ViolationResponse>('/machines/impact-alarm-violations?window_days=7')
      .then(data => { setRows(data?.points || []); setWindowDays(data?.window_days ?? 7); setError(false); })
      .catch(() => { setRows([]); setError(true); })
      .finally(() => setLoading(false));
  }, []);

  const databases = Array.from(
    new Map(rows.map(r => [r.db_id, r.company_name || String(r.db_id)])).entries()
  ).sort((a, b) => a[1].localeCompare(b[1]));

  const filtered = rows.filter(r => dbFilter === null || r.db_id === dbFilter);

  const severityRank = (r: ViolationRow) =>
    r.violations_severe_7d * 1e9 + r.violations_moderate_7d * 1e3 + (r.latest_max_peak_g ?? 0);

  const sorted = [...filtered].sort((a, b) => {
    let cmp = 0;
    if (sortKey === 'severity') cmp = severityRank(b) - severityRank(a);
    else if (sortKey === 'machine_name') cmp = a.machine_name.localeCompare(b.machine_name);
    else if (sortKey === 'threshold') cmp = (b.threshold_moderate ?? 0) - (a.threshold_moderate ?? 0);
    else if (sortKey === 'latest_max_peak_g') cmp = (b.latest_max_peak_g ?? 0) - (a.latest_max_peak_g ?? 0);
    else if (sortKey === 'periodic_pct') cmp = (b.periodic_pct ?? -1) - (a.periodic_pct ?? -1);
    else if (sortKey === 'concentration_ratio') cmp = (b.concentration_ratio ?? -1) - (a.concentration_ratio ?? -1);
    else if (sortKey === 'harmonic_fraction') cmp = (b.harmonic_fraction ?? -1) - (a.harmonic_fraction ?? -1);
    else if (sortKey === 'confidence') {
      const o: Record<string, number> = { high: 1, medium: 2, low: 3 };
      cmp = (o[a.confidence] ?? 4) - (o[b.confidence] ?? 4);
    }
    else if (sortKey === 'measured_at') cmp = (a.measured_at ?? '').localeCompare(b.measured_at ?? '');
    // 'severity' comparator is already desc-oriented; apply dir uniformly.
    return sortDir === 'desc' ? cmp : -cmp;
  });

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const SortHeader = ({ col, label, className = '', tooltip }: { col: SortKey; label: string; className?: string; tooltip?: string }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer select-none hover:text-gray-700 ${className}`}
      onClick={() => handleSort(col)}
    >
      <span className="flex items-center gap-1">
        {label}
        {sortKey === col && <span className="text-[#dc2626]">{sortDir === 'desc' ? '↓' : '↑'}</span>}
        {tooltip && <ColTooltip text={tooltip} />}
      </span>
    </th>
  );

  // Header summary: N points in alarm · X with a severe violation · Y moderate-only.
  const nPoints = filtered.length;
  const nSevere = filtered.filter(r => r.violations_severe_7d > 0).length;
  const nModerateOnly = nPoints - nSevere;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Impact Score</h1>
            <p className="text-sm text-gray-500">Impact View alarm violations — last {windowDays} days</p>
          </div>
        </div>

        {/* Violation summary */}
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 font-medium">
            {nPoints} point{nPoints === 1 ? '' : 's'} in alarm
          </span>
          <span className="px-2.5 py-1 rounded-full bg-red-50 text-red-700 font-medium">
            {nSevere} severe
          </span>
          <span className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 font-medium">
            {nModerateOnly} moderate-only
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        {databases.length > 1 && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Database:</span>
            {databases.map(([id, name]) => (
              <button
                key={id}
                onClick={() => setDbFilter(dbFilter === id ? null : id)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                  dbFilter === id
                    ? 'bg-gray-800 text-white border-gray-800'
                    : 'text-gray-600 border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        )}
        {dbFilter !== null && (
          <button onClick={() => setDbFilter(null)} className="text-xs text-gray-400 hover:text-gray-600 underline">
            Clear filters
          </button>
        )}
        <span className="text-xs text-gray-400 ml-auto">{sorted.length} shown</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="h-64 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="p-6">
            <DataUnavailable message="Impact View alarm violations could not be loaded." />
          </div>
        ) : sorted.length === 0 ? (
          <div className="h-64 flex flex-col items-center justify-center text-gray-400 gap-2">
            <AlertTriangle className="w-8 h-8 opacity-40" />
            <p className="text-sm">No Impact View alarm violations in the last {windowDays} days.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <SortHeader col="machine_name" label="Machine" />
                  <SortHeader col="latest_max_peak_g" label="Latest MaxPk"
                    tooltip="Most recent PeakVue stress-wave amplitude on the worst axis. ▲ rising / ▼ falling = MaxPk trajectory over 90 days (r²-gated)." />
                  <SortHeader col="threshold" label="Alarm Threshold"
                    tooltip="Per-axis PeakVue alarm thresholds on the point's worst axis, in G. Moderate = the first-alarm line (Mean+3σ from the axis's own history); Severe = 1.5× moderate. 'Emerson' = site-default fallback used when the axis lacks enough history." />
                  <SortHeader col="severity" label="Violations (7d)"
                    tooltip="Captures over each threshold on the worst axis in the trailing 7 days. Severe = readings ≥ the severe threshold (a subset of moderate). The table is sorted by severe, then moderate, then latest MaxPk." />
                  <SortHeader col="periodic_pct" label="Periodicity %"
                    tooltip="How repetitive the impact pattern is on the latest reading. ≥30% suggests a structured fault; ≥50% is strongly fault-like." />
                  <SortHeader col="concentration_ratio" label="Concentration"
                    tooltip="Fraction of impact energy concentrated in specific frequency bands vs. broadband noise, from the most recent reading." />
                  <SortHeader col="harmonic_fraction" label="Harmonics"
                    tooltip="Fraction of spectral energy organized into a harmonic series, from the most recent reading. High values confirm a structured, repeating fault source." />
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    <span className="flex items-center gap-1">
                      Harmonic Series
                      <ColTooltip text="Fundamental CPM where the harmonic comb begins. Color = rate of change of harmonic energy over ~30 days: red rising, amber falling (late-stage or improving — verify), gray stable. Requires 6+ running captures." />
                    </span>
                  </th>
                  <SortHeader col="confidence" label="Confidence"
                    tooltip="Reliability of the assessment — based on the number of PeakVue readings on the worst axis in the last 30 days. High = ≥10 · Medium = 5–9 · Low = <5." />
                  <SortHeader col="measured_at" label="Last Reading" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sorted.map(row => {
                  const key = `${row.machine_id}:${row.point_index}`;
                  return (
                    <tr
                      key={key}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => onMachineClick(row.machine_id, row.machine_name, [], undefined, row.point_name ?? undefined)}
                    >
                      {/* Machine + breadcrumb + point (worst axis) */}
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{row.machine_name}</div>
                        <div className="text-xs text-gray-400">{row.company_name} › {row.area_name}</div>
                        <div className="text-xs text-blue-600 mt-0.5 font-medium">
                          {row.point_name}{row.axis ? ` (${row.axis})` : ''}
                        </div>
                      </td>

                      {/* Latest MaxPk + ROC arrow (moved next to the machine hierarchy) */}
                      <td className="px-4 py-3">
                        {row.latest_max_peak_g != null ? (() => {
                          const roc = row.maxpk_roc;
                          const s = roc?.roc_state;
                          const tip = roc && roc.slope_pct_wk != null
                            ? `MaxPk trajectory: ${roc.slope_pct_wk >= 0 ? '+' : ''}${roc.slope_pct_wk}%/wk over 90d (r²=${roc.r2}, n=${roc.n_captures})`
                            : s === 'insufficient' ? `MaxPk trajectory: too few captures (${roc?.n_captures ?? 0})` : undefined;
                          return (
                            <span className="inline-flex items-center gap-1" title={tip}>
                              <span className={`text-xs font-medium ${
                                row.latest_severity === 'urgent'      ? 'text-red-600'
                              : row.latest_severity === 'action'      ? 'text-orange-600'
                              : row.latest_severity === 'investigate' ? 'text-yellow-600'
                              : row.latest_severity === 'monitor'     ? 'text-amber-500'
                              : 'text-gray-600'}`}>
                                {row.latest_max_peak_g.toFixed(1)}G
                              </span>
                              {(s === 'rising' || s === 'rising_fast') && <span className="text-red-600 text-xs leading-none">▲</span>}
                              {s === 'falling' && <span className="text-green-600 text-xs leading-none">▼</span>}
                            </span>
                          );
                        })() : <span className="text-xs text-gray-400">—</span>}
                      </td>

                      {/* Alarm Threshold (worst axis) — stacked mod / sev to save width */}
                      <td className="px-4 py-3 text-xs whitespace-nowrap leading-tight">
                        <div><span className="text-amber-600 font-medium">{row.threshold_moderate?.toFixed(1)} G</span> <span className="text-gray-400">mod</span></div>
                        <div><span className="text-red-600 font-medium">{row.threshold_severe?.toFixed(1)} G</span> <span className="text-gray-400">sev</span></div>
                        {row.is_emerson_fallback && (
                          <div className="text-[9px] text-gray-400 uppercase tracking-wide">Emerson</div>
                        )}
                      </td>

                      {/* Violations (7d) — stacked severe / moderate to save width */}
                      <td className="px-4 py-3 text-xs whitespace-nowrap leading-tight">
                        {row.violations_severe_7d > 0 && (
                          <div className="font-semibold text-red-600">{row.violations_severe_7d}× severe</div>
                        )}
                        <div className="font-medium text-amber-600">{row.violations_moderate_7d}× moderate</div>
                      </td>

                      {/* Periodicity % */}
                      <td className="px-4 py-3 text-xs">
                        {row.periodic_pct != null
                          ? <span className={`font-medium ${row.periodic_pct >= 50 ? 'text-red-600' : row.periodic_pct >= 30 ? 'text-orange-500' : 'text-gray-600'}`}>{row.periodic_pct.toFixed(0)}%</span>
                          : <span className="text-gray-400">—</span>}
                      </td>

                      {/* Concentration */}
                      <td className="px-4 py-3 text-xs">
                        {row.concentration_ratio != null
                          ? <span className={`font-medium ${row.concentration_ratio >= 0.4 ? 'text-red-600' : row.concentration_ratio >= 0.2 ? 'text-orange-500' : 'text-gray-600'}`}>{(row.concentration_ratio * 100).toFixed(0)}%</span>
                          : <span className="text-gray-400">—</span>}
                      </td>

                      {/* Harmonics */}
                      <td className="px-4 py-3 text-xs">
                        {row.harmonic_fraction != null
                          ? <span className={`font-medium ${row.harmonic_fraction >= 0.6 ? 'text-red-600' : row.harmonic_fraction >= 0.4 ? 'text-orange-500' : 'text-gray-600'}`}>{(row.harmonic_fraction * 100).toFixed(0)}%</span>
                          : <span className="text-gray-400">—</span>}
                      </td>

                      {/* Harmonic Series cell */}
                      <td className="px-4 py-3">
                        {row.harmonic_column
                          ? <HarmonicSeriesCell data={row.harmonic_column} />
                          : <span className="text-gray-300 text-xs">—</span>}
                      </td>

                      {/* Confidence */}
                      <td className="px-4 py-3"><ConfidencePip level={row.confidence} /></td>

                      {/* Last reading (worst axis, latest violation window capture) */}
                      <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{fmtEastern(row.measured_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
